import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { CALENDAR_GA_EVENT_ACTION } from 'constants/analytics'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import { shouldEnableCalendarRecommendations } from 'selectors/featuresSelectors'
import { isAnyMultiRoomFlightsActive } from 'selectors/offerPage/multipleRoomBookingSelectors'
import {
  OfferPageActions,
  takeCurrentOfferState,
} from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { isDateBetween } from 'lib/datetime/dateUtils'
import OfferCalendarSidebarDayCell, { OfferCalendarSidebarDayCellFeatured, OfferCalendarSidebarDayCellType, OfferCalendarSidebarDayCellVariant } from './OfferCalendarSidebarDayCell'
import ModalContext from 'contexts/ModalContext'
import OfferCalendarPickerRecommendationsModal from '../CalendarPicker/OfferCalendarPickerRecommendationsModal'
import { getSidebarCalendarMonthEmptyCells, getLocalDate } from 'lib/datetime/calendarUtils'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'

const BaseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7 , 1fr);
`

interface Props {
  days: Array<App.CalendarDay>;
  cheapestPrice: number;
  otherAvailability: { [checkIn: string]: boolean };
  selectedDay?: App.CalendarDay;
  isAnyFlightsActive: boolean;
  currentOfferId?: string;
  enableCalendarRecommendations?: boolean;
  displayMemberPrice?: boolean;
}

function OfferCalendarSidebarDaysMonth(props: Props) {
  const {
    days,
    cheapestPrice,
    enableCalendarRecommendations,
    otherAvailability,
    selectedDay,
    isAnyFlightsActive,
    currentOfferId,
    displayMemberPrice,
  } = props

  const canLoyaltyUpgrade = useUserHasLoyaltyBenefit('hotel_upgrade')

  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const dispatch = useOfferPageDispatch()
  const showModal = useContext(ModalContext)
  const onDayClick = useCallback((day: App.CalendarDay) => {
    const available = isAnyFlightsActive ? day.flightsAvailable && day.hotelAvailable : day.hotelAvailable
    if (available) {
      dispatch({ type: OfferPageActions.setCheckInDate, checkIn: day.checkIn, offerId: currentOfferId })
    } else if (isAnyFlightsActive && !available && day.hotelAvailable) {
      dispatch({ type: OfferPageActions.toggleFlightsPrice, isFlightsSelected: false })
    } else if (otherAvailability[day.checkIn]) {
      dispatch({ type: OfferPageActions.toggleSubFilter, filter: 'room' })
    } else if (enableCalendarRecommendations) {
      showModal(<OfferCalendarPickerRecommendationsModal checkIn={day.checkIn}/>)
    }
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CheckIn, day.checkIn)
  }, [currentOfferId, dispatch, enableCalendarRecommendations, isAnyFlightsActive, onUserAnalyticsAction, otherAvailability, showModal,
  ])

  const firstDate = days[0]
  // our month may not start on the first day of the month, because we can't check in
  // on those days (they may be in the past). So we fill in the earliest weeks cells
  const emptyCells = useMemo(() => getSidebarCalendarMonthEmptyCells(firstDate), [firstDate])

  const startCellStyle = useMemo(() => {
    return {
      // we need to align the first day of the month with our day heading
      // e.g. if the first day of a month is a wednesday, it should start under wednesday!
      gridColumnStart: getLocalDate(firstDate.checkIn).getDay() === 0 ? 7 : getLocalDate(firstDate.checkIn).getDay(), // Sunday goes to the end of the week
    }
  }, [firstDate])

  return (
    <BaseGrid>
      {emptyCells.map((cell) => <OfferCalendarSidebarDayCell
        key={cell.day}
        day={cell as any}
        style={{ gridColumnStart: cell.column }}
        variant="unavailable"
        type="none"
      />)}
      {days.map((day, index) => {
        let type: OfferCalendarSidebarDayCellType = 'none'
        if (selectedDay) {
          if (day.checkIn === selectedDay.checkIn) {
            type = 'check-in'
          } else if (day.checkIn === selectedDay.checkOut) {
            type = 'check-out'
          } else if (isDateBetween(new Date(selectedDay.checkIn), new Date(selectedDay.checkOut), new Date(day.checkIn))) {
            type = 'between'
          }
        }

        let price = isAnyFlightsActive ? day.packagedTotal : day.hotelTotal
        if (displayMemberPrice) {
          price = isAnyFlightsActive ? day.packagedMemberTotal : day.hotelMemberTotal
        }

        let variant: OfferCalendarSidebarDayCellVariant = 'none'
        const available = isAnyFlightsActive ? day.flightsAvailable && day.hotelAvailable : day.hotelAvailable
        if (available) {
          if (displayMemberPrice) {
            variant = 'price-member'
          } else if (price === cheapestPrice) {
            variant = 'price-cheapest'
          } else {
            variant = 'price'
          }
        } else if (isAnyFlightsActive && !available && day.hotelAvailable) {
          variant = 'sold-out-hotel'
        } else if (otherAvailability[day.checkIn]) {
          variant = 'sold-out-rooms'
        } else if (enableCalendarRecommendations) {
          variant = 'sold-out-recommendations'
        } else {
          variant = 'unavailable'
        }

        let featured: OfferCalendarSidebarDayCellFeatured | undefined = undefined
        if (available) {
          if (day.hasTactical || !!day.dealId || (day.hasFlightDeal && isAnyFlightsActive)) {
            featured = 'special'
          } else if (day.hasLoyaltyUpgrade && canLoyaltyUpgrade) {
            featured = 'loyalty'
          }
        }

        return <OfferCalendarSidebarDayCell
          key={day.checkIn}
          style={emptyCells.length === 0 && index === 0 ? startCellStyle : undefined}
          day={day}
          flights={isAnyFlightsActive}
          onClick={onDayClick}
          type={type}
          variant={variant}
          price={price}
          featured={featured}
        />
      })}
    </BaseGrid>
  )
}

export default withOfferPageAndReduxState((currentState, appState) => {
  const state = takeCurrentOfferState(currentState)
  const isMultiRoom = isMultipleRoomBookingActive(state, appState)
  return {
    enableCalendarRecommendations: !state.bundleOffer ? shouldEnableCalendarRecommendations(state.offer) : false,
    isAnyFlightsActive: isMultiRoom ? isAnyMultiRoomFlightsActive(state, appState) : state.flights,
    displayMemberPrice: currentState.displayMemberPrice,
  }
}, OfferCalendarSidebarDaysMonth)
